import React, { useEffect } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { useField } from 'react-final-form';
import { AxiosError } from 'axios';
import { DatePicker, TimePicker } from '../components';
import { Control } from './Control';

interface Props {
  name: string;
  className?: string;
  datesLoading: boolean;
  datesError?: AxiosError<unknown>;
  includeDays: Date[];
  timesLoading?: boolean;
  timesError?: AxiosError<unknown>;
  includeTimes?: Date[];
  day: Date | null;
  setDay: React.Dispatch<React.SetStateAction<Date | null>>;
  onMonthChange: (date: Date) => void;
  isWeekView?: boolean;
  setWeekView?: React.Dispatch<React.SetStateAction<boolean>>;
  dayOnly?: boolean;
  slotOnly?: boolean;
}

export const DateTimePicker: React.FC<Props> = ({
  className,
  name,
  datesLoading,
  datesError,
  includeDays,
  timesLoading,
  timesError,
  includeTimes,
  day,
  setDay,
  onMonthChange,
  isWeekView = false,
  setWeekView,
  dayOnly = false,
  slotOnly = false,
}) => {
  const { input } = useField(name);

  useEffect(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const filterDays = includeDays
      .filter((d) => {
        const currentDate = new Date(d);
        return currentDate.getTime() >= today.getTime();
      })
      .sort((a, b) => (a.getTime() > b.getTime() ? 1 : -1));
    if (filterDays?.length) {
      setDay(filterDays[0]);
    }
  }, [setDay, includeDays]);

  const onDayChange = (date: Date) => {
    input.onChange(undefined);
    setDay(date);
  };

  const onTimeChange = (time: Date) => {
    if (day) {
      const [year, month, date] = [
        day.getFullYear(),
        day.getMonth(),
        day.getDate(),
      ];

      const [hours, minutes] = [time.getHours(), time.getMinutes()];

      input.onChange(new Date(year, month, date, hours, minutes, 0));
    }
  };

  return (
    <Control name={name} mb={4} className={className}>
      <Flex
        gridGap={6}
        flexDirection={dayOnly ? 'column' : { base: 'column', lg: 'row' }}
      >
        {!slotOnly && (
          <Box>
            <DatePicker
              loading={datesLoading}
              error={datesError}
              include={includeDays}
              value={day}
              onChange={onDayChange}
              onMonthChange={onMonthChange}
              isWeekView={isWeekView}
              setWeekView={setWeekView}
            />
          </Box>
        )}

        {!!day && !dayOnly && (
          <Box flex="auto">
            <TimePicker
              loading={timesLoading}
              error={timesError}
              selectedDay={day}
              value={input.value}
              include={includeTimes}
              onChange={onTimeChange}
            />
          </Box>
        )}
      </Flex>
    </Control>
  );
};
